import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, ArrowLeft, Pencil, Power } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { api, type PromoCode, type PromoStats } from "@/api";
import { AudienceBadge, CopyButton, dateRu, dateTimeRu, discountLabel, draftFromPromo, ExportButtons, num, ONBOARDING_LABEL, ORDER_GROUP_CLASS, ORDER_GROUP_LABEL, PeriodFilter, type Period, Pill, pct, PromoEditorDialog, rewardLabel, rub, saveFile, sourceLabel, StateBadge, StatTile } from "@/components/promo-kit";
import { axisProps, ChartContainer, ChartLegend, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCanManagePromos } from "./promos";

/** Карточка промокода: условия, ссылки, статистика за период, выгрузка. */
export function PromoDetailPage() {
  const $ = _c(107);
  const {
    code: t0
  } = useParams();
  const code = t0 === undefined ? "" : t0;
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  let t1;
  if ($[0] !== params) {
    t1 = params.get("from") ?? "";
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== params) {
    t2 = params.get("to") ?? "";
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      from: t1,
      to: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const period = t3;
  let t4;
  if ($[7] !== params || $[8] !== setParams) {
    t4 = p => {
      const next = new URLSearchParams(params);
      if (p.from) {
        next.set("from", p.from);
      } else {
        next.delete("from");
      }
      if (p.to) {
        next.set("to", p.to);
      } else {
        next.delete("to");
      }
      setParams(next, {
        replace: true
      });
    };
    $[7] = params;
    $[8] = setParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const setPeriod = t4;
  let t5;
  if ($[10] !== code) {
    t5 = {
      queryKey: ["promos", "detail", code],
      queryFn: () => api.promos.get(code)
    };
    $[10] = code;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const promoQ = useQuery(t5);
  let t6;
  if ($[12] !== code || $[13] !== period.from || $[14] !== period.to) {
    t6 = ["promos", "stats", code, period.from, period.to];
    $[12] = code;
    $[13] = period.from;
    $[14] = period.to;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== code || $[17] !== period) {
    t7 = () => api.promos.stats(code, period);
    $[16] = code;
    $[17] = period;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7
    };
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const statsQ = useQuery(t8);
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const canManage = useCanManagePromos();
  const promo = promoQ.data;
  const stats = statsQ.data;
  let t9;
  if ($[22] !== queryClient) {
    t9 = () => queryClient.invalidateQueries({
      queryKey: ["promos"]
    });
    $[22] = queryClient;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const refresh = t9;
  if (promoQ.error) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <BackLink />;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    const t11 = promoQ.error as Error;
    let t12;
    if ($[25] !== t11.message) {
      t12 = <div className="flex flex-col gap-4">{t10}<p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{t11.message}</p></div>;
      $[25] = t11.message;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    return t12;
  }
  if (!promo) {
    let t10;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="text-sm text-muted-foreground">Загружаем…</p>;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  let t10;
  if ($[28] !== promo.code || $[29] !== refresh) {
    t10 = async body => {
      setActionError(null);
      ;
      try {
        await api.promos.update(promo.code, body);
        await refresh();
      } catch (t11) {
        const e = t11;
        setActionError((e as Error).message);
      }
    };
    $[28] = promo.code;
    $[29] = refresh;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const patch = t10;
  const archived = promo.state === "archived";
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <BackLink />;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== promo.code) {
    t12 = <h1 className="font-mono text-2xl font-semibold">{promo.code}</h1>;
    $[32] = promo.code;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== promo.audience) {
    t13 = <AudienceBadge audience={promo.audience} />;
    $[34] = promo.audience;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== promo.state) {
    t14 = <StateBadge state={promo.state} />;
    $[36] = promo.state;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t14) {
    t15 = <div className="flex flex-wrap items-center gap-2">{t12}{t13}{t14}</div>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== promo.partner) {
    t16 = promo.partner ? <>Владелец:{" "}<Link className="text-foreground hover:underline" to={`/promos/partners/${promo.partner.id}`} data-testid="promo-partner-link">{promo.partner.name}</Link></> : "\u041A\u043E\u0434 \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0438, \u0431\u0435\u0437 \u043F\u0430\u0440\u0442\u043D\u0451\u0440\u0430";
    $[42] = promo.partner;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== promo.description) {
    t17 = promo.description && <> · {promo.description}</>;
    $[44] = promo.description;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <div className="text-sm text-muted-foreground">{t16}{t17}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t15 || $[50] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t15}{t18}</div>;
    $[49] = t15;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== promo.audience || $[53] !== promo.links) {
    t20 = promo.links && <CopyButton value={promo.audience === "seller" ? promo.links.sellerRegister : promo.links.storefront} label={promo.audience === "seller" ? "\u0421\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E" : "\u0421\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0432\u0438\u0442\u0440\u0438\u043D\u0443"} testId="promo-copy-link" />;
    $[52] = promo.audience;
    $[53] = promo.links;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== archived || $[56] !== canManage || $[57] !== patch || $[58] !== promo.active) {
    t21 = canManage && !archived && <><Button variant="outline" size="sm" onClick={() => void patch({
        active: !promo.active
      })}><Power className="mr-1.5 size-4" />{promo.active ? "\u0412\u044B\u043A\u043B\u044E\u0447\u0438\u0442\u044C" : "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C"}</Button><Button variant="outline" size="sm" onClick={() => void patch({
        archived: true
      })} data-testid="promo-archive"><Archive className="mr-1.5 size-4" />В архив</Button><Button size="sm" onClick={() => setEditing(true)} data-testid="promo-edit"><Pencil className="mr-1.5 size-4" />Изменить</Button></>;
    $[55] = archived;
    $[56] = canManage;
    $[57] = patch;
    $[58] = promo.active;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== archived || $[61] !== canManage || $[62] !== patch) {
    t22 = canManage && archived && <Button size="sm" variant="outline" onClick={() => void patch({
      archived: false
    })}><ArchiveRestore className="mr-1.5 size-4" />Вернуть из архива</Button>;
    $[60] = archived;
    $[61] = canManage;
    $[62] = patch;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t20 || $[65] !== t21 || $[66] !== t22) {
    t23 = <div className="flex flex-wrap gap-2">{t20}{t21}{t22}</div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t19 || $[69] !== t23) {
    t24 = <div className="flex flex-wrap items-start justify-between gap-4">{t19}{t23}</div>;
    $[68] = t19;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== actionError) {
    t25 = actionError && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{actionError}</p>;
    $[71] = actionError;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== promo) {
    t26 = <Conditions promo={promo} />;
    $[73] = promo;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== stats) {
    t27 = stats ? {
      from: stats.from,
      to: stats.to
    } : undefined;
    $[75] = stats;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== period || $[78] !== setPeriod || $[79] !== t27) {
    t28 = <PeriodFilter value={period} onChange={setPeriod} resolved={t27} />;
    $[77] = period;
    $[78] = setPeriod;
    $[79] = t27;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== period || $[82] !== promo.code) {
    t29 = <ExportButtons onExport={async format => saveFile(await api.promos.exportStats(promo.code, period, format))} />;
    $[81] = period;
    $[82] = promo.code;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== t28 || $[85] !== t29) {
    t30 = <div className="flex flex-wrap items-center justify-between gap-3">{t28}{t29}</div>;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== stats) {
    t31 = stats && <p className="-mt-3 text-xs text-muted-foreground">Период: {dateRu(`${stats.from}T12:00:00Z`)} — {dateRu(`${stats.to}T12:00:00Z`)}. Засчитаны оплаченные и не отменённые заказы; возвраты вычитаются суммой возвращённых товаров.</p>;
    $[87] = stats;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== statsQ.error) {
    t32 = statsQ.error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{(statsQ.error as Error).message}</p>;
    $[89] = statsQ.error;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== promo || $[92] !== stats) {
    t33 = stats && <StatsBody promo={promo} stats={stats} />;
    $[91] = promo;
    $[92] = stats;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== editing || $[95] !== promo || $[96] !== refresh) {
    t34 = editing && <PromoEditorDialog open={true} editingCode={promo.code} initial={draftFromPromo(promo)} onClose={() => setEditing(false)} onSaved={async () => {
      setEditing(false);
      await refresh();
    }} />;
    $[94] = editing;
    $[95] = promo;
    $[96] = refresh;
    $[97] = t34;
  } else {
    t34 = $[97];
  }
  let t35;
  if ($[98] !== t24 || $[99] !== t25 || $[100] !== t26 || $[101] !== t30 || $[102] !== t31 || $[103] !== t32 || $[104] !== t33 || $[105] !== t34) {
    t35 = <div className="flex flex-col gap-6" data-testid="promo-detail">{t11}{t24}{t25}{t26}{t30}{t31}{t32}{t33}{t34}</div>;
    $[98] = t24;
    $[99] = t25;
    $[100] = t26;
    $[101] = t30;
    $[102] = t31;
    $[103] = t32;
    $[104] = t33;
    $[105] = t34;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  return t35;
}
function BackLink() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link to="/promos" className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="size-4" />Промокоды</Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Conditions({
  promo
}: {
  promo: PromoCode;
}) {
  const seller = promo.audience === "seller";
  const rows: [string, string][] = seller ? [["Скидка", "Нет — код отмечает, чей продавец"], ["Вознаграждение партнёра", rewardLabel(promo)], ["Срок", validity(promo)]] : [["Скидка", discountLabel(promo)], ["Мин. сумма", promo.minSubtotal > 0 ? rub(promo.minSubtotal) : "нет"], ["Лимиты", `${promo.maxUses == null ? "всего без лимита" : `всего ${num(promo.usedCount)} из ${num(promo.maxUses)}`}, ${promo.perUserLimit == null ? "на покупателя без лимита" : `на покупателя ${promo.perUserLimit}`}`], ["Только новым на OSMIX", promo.firstOrderOnly ? "да" : "нет"], ["Только кто не покупал в магазине", promo.newToShopOnly ? "да" : "нет"], ["Ограничения", [promo.shopIds.length ? `магазинов: ${promo.shopIds.length}` : "", promo.categoryIds.length ? `категорий: ${promo.categoryIds.length}` : "", promo.productIds?.length ? `товаров: ${promo.productIds.length}` : ""].filter(Boolean).join(", ") || "на все товары"], ["Срок", validity(promo)], ["Вознаграждение партнёра", rewardLabel(promo)],
  // Механики П2: чей код и кто платит за скидку.
  ["Владелец", promo.userId ? `персональный: ${promo.userName || promo.userId}` : promo.shopId ? `магазин «${promo.shopName ?? promo.shopId}» — скидка за счёт магазина` : "площадка — скидка за счёт площадки"], ["В «Мои купоны»", promo.isPublic ? "да" : "нет"], ["При полной отмене заказа", promo.restoreOnCancel ? "код возвращается" : "не возвращается"]];
  return <div className="grid gap-x-8 gap-y-2 rounded-lg border p-4 text-sm sm:grid-cols-2 lg:grid-cols-3" data-testid="promo-conditions">
			{rows.map(([label, value]) => <div key={label} className="flex justify-between gap-3 sm:block">
					<div className="text-xs text-muted-foreground">{label}</div>
					<div>{value}</div>
				</div>)}
			{promo.links && <div className="sm:col-span-2 lg:col-span-3">
					<div className="text-xs text-muted-foreground">{seller ? "Ссылка на регистрацию продавца" : "Ссылка на витрину"}</div>
					<div className="break-all font-mono text-xs">{seller ? promo.links.sellerRegister : promo.links.storefront}</div>
				</div>}
		</div>;
}
const validity = (p: PromoCode): string => !p.startsAt && !p.expiresAt ? "бессрочно" : `${p.startsAt ? dateTimeRu(p.startsAt) : "…"} — ${p.expiresAt ? dateTimeRu(p.expiresAt) : "…"}`;
const shortDay = (day: string) => day.split("-").reverse().slice(0, 2).join(".");
function StatsBody(t0) {
  const $ = _c(40);
  const {
    promo,
    stats
  } = t0;
  const t = stats.totals;
  const seller = promo.audience === "seller";
  let t1;
  if ($[0] !== promo || $[1] !== seller || $[2] !== t.applies || $[3] !== t.attractedBuyers || $[4] !== t.attractedGmv || $[5] !== t.avgCheck || $[6] !== t.buyers || $[7] !== t.conversion || $[8] !== t.discount || $[9] !== t.gmv || $[10] !== t.itemsSold || $[11] !== t.newBuyers || $[12] !== t.orders || $[13] !== t.refundAmount || $[14] !== t.refunds || $[15] !== t.repeatBuyers || $[16] !== t.revenue || $[17] !== t.reward || $[18] !== t.sellerCommission || $[19] !== t.sellerGmv || $[20] !== t.sellerOrders || $[21] !== t.sellerProducts || $[22] !== t.sellerQty || $[23] !== t.sellersApproved || $[24] !== t.sellersRegistered || $[25] !== t.sellersStarted || $[26] !== t.visits) {
    t1 = <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-testid="promo-kpis">{seller ? <><StatTile label={"\u041F\u0435\u0440\u0435\u0445\u043E\u0434\u044B \u043F\u043E \u0441\u0441\u044B\u043B\u043A\u0435"} value={num(t.visits)} /><StatTile label={"\u041D\u0430\u0447\u0430\u043B\u0438 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E"} value={num(t.sellersStarted)} /><StatTile label={"\u0417\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043B\u0438\u0441\u044C"} value={num(t.sellersRegistered)} /><StatTile label={"\u041F\u0440\u043E\u0448\u043B\u0438 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E"} value={num(t.sellersApproved)} /><StatTile label={"\u0422\u043E\u0432\u0430\u0440\u043E\u0432 \u043D\u0430 \u0432\u0438\u0442\u0440\u0438\u043D\u0435"} value={num(t.sellerProducts)} hint={"\u0441\u0435\u0439\u0447\u0430\u0441"} /><StatTile label={"\u0417\u0430\u043A\u0430\u0437\u044B"} value={num(t.sellerOrders)} /><StatTile label={"\u041F\u0440\u043E\u0434\u0430\u043D\u043E, \u0448\u0442."} value={num(t.sellerQty)} /><StatTile label={"\u041F\u0440\u043E\u0434\u0430\u0436\u0438 (GMV)"} value={rub(t.sellerGmv)} /><StatTile label={"\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u044F \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0438"} value={rub(t.sellerCommission)} /><StatTile label={"\u041D\u0430\u0447\u0438\u0441\u043B\u0435\u043D\u043E \u043F\u0430\u0440\u0442\u043D\u0451\u0440\u0443"} value={rub(t.reward)} hint={rewardLabel(promo)} /></> : <><StatTile label={"\u0417\u0430\u043A\u0430\u0437\u044B"} value={num(t.orders)} hint={`${num(t.itemsSold)} шт. товаров`} /><StatTile label={"\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u0438"} value={num(t.buyers)} hint={`новых ${num(t.newBuyers)} · повторных ${num(t.repeatBuyers)}`} /><StatTile label={"\u0422\u043E\u0432\u0430\u0440\u044B \u0434\u043E \u0441\u043A\u0438\u0434\u043A\u0438"} value={rub(t.gmv)} /><StatTile label={"\u0421\u043A\u0438\u0434\u043A\u0438"} value={rub(t.discount)} /><StatTile label={"\u0412\u044B\u0440\u0443\u0447\u043A\u0430 \u043F\u043E\u0441\u043B\u0435 \u0441\u043A\u0438\u0434\u043A\u0438"} value={rub(t.revenue)} /><StatTile label={"\u0421\u0440\u0435\u0434\u043D\u0438\u0439 \u0447\u0435\u043A"} value={rub(t.avgCheck)} /><StatTile label={"\u0412\u043E\u0437\u0432\u0440\u0430\u0442\u044B"} value={rub(t.refundAmount)} hint={`заказов: ${num(t.refunds)}`} /><StatTile label={"\u0412\u043E\u0440\u043E\u043D\u043A\u0430"} value={`${num(t.visits)} → ${num(t.applies)} → ${num(t.orders)}`} hint={`переходы → применения → заказы · конверсия ${pct(t.conversion)}`} /><StatTile label={"\u041F\u0440\u0438\u0432\u043B\u0435\u0447\u0435\u043D\u043E \u043F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u0435\u0439"} value={num(t.attractedBuyers)} hint={`их покупки: ${rub(t.attractedGmv)}`} /><StatTile label={"\u041D\u0430\u0447\u0438\u0441\u043B\u0435\u043D\u043E \u043F\u0430\u0440\u0442\u043D\u0451\u0440\u0443"} value={rub(t.reward)} hint={rewardLabel(promo)} /></>}</div>;
    $[0] = promo;
    $[1] = seller;
    $[2] = t.applies;
    $[3] = t.attractedBuyers;
    $[4] = t.attractedGmv;
    $[5] = t.avgCheck;
    $[6] = t.buyers;
    $[7] = t.conversion;
    $[8] = t.discount;
    $[9] = t.gmv;
    $[10] = t.itemsSold;
    $[11] = t.newBuyers;
    $[12] = t.orders;
    $[13] = t.refundAmount;
    $[14] = t.refunds;
    $[15] = t.repeatBuyers;
    $[16] = t.revenue;
    $[17] = t.reward;
    $[18] = t.sellerCommission;
    $[19] = t.sellerGmv;
    $[20] = t.sellerOrders;
    $[21] = t.sellerProducts;
    $[22] = t.sellerQty;
    $[23] = t.sellersApproved;
    $[24] = t.sellersRegistered;
    $[25] = t.sellersStarted;
    $[26] = t.visits;
    $[27] = t1;
  } else {
    t1 = $[27];
  }
  let t2;
  let t3;
  if ($[28] !== seller || $[29] !== stats) {
    t2 = <DailyChart stats={stats} seller={seller} />;
    t3 = seller && <SellersTable stats={stats} />;
    $[28] = seller;
    $[29] = stats;
    $[30] = t2;
    $[31] = t3;
  } else {
    t2 = $[30];
    t3 = $[31];
  }
  let t4;
  if ($[32] !== seller || $[33] !== stats) {
    t4 = (!seller || stats.orders.length > 0) && <><OrdersTable stats={stats} /><div className="grid gap-6 xl:grid-cols-2"><BuyersTable stats={stats} /><div className="flex min-w-0 flex-col gap-6"><TopProducts stats={stats} /><TopShops stats={stats} /></div></div></>;
    $[32] = seller;
    $[33] = stats;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  let t5;
  if ($[35] !== t1 || $[36] !== t2 || $[37] !== t3 || $[38] !== t4) {
    t5 = <div className="flex flex-col gap-6">{t1}{t2}{t3}{t4}</div>;
    $[35] = t1;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  return t5;
}
function DailyChart(t0) {
  const $ = _c(45);
  const {
    stats,
    seller
  } = t0;
  let t1;
  if ($[0] !== seller) {
    t1 = seller ? {
      registrations: {
        label: "\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u0438",
        color: "var(--chart-1, #FA5C2E)"
      },
      approvals: {
        label: "\u041F\u0440\u043E\u0448\u043B\u0438 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E",
        color: "var(--chart-2, #2E7D32)"
      }
    } : {
      orders: {
        label: "\u0417\u0430\u043A\u0430\u0437\u044B",
        color: "var(--chart-1, #FA5C2E)"
      },
      applies: {
        label: "\u041F\u0440\u0438\u043C\u0435\u043D\u0435\u043D\u0438\u044F",
        color: "var(--chart-2, #9AA0A6)"
      }
    };
    $[0] = seller;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const config = t1;
  let t2;
  if ($[2] !== seller) {
    t2 = seller ? {
      sellerGmv: {
        label: "\u041F\u0440\u043E\u0434\u0430\u0436\u0438 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432, \u20BD",
        color: "var(--chart-3, #1F1F1F)"
      }
    } : {
      revenue: {
        label: "\u0412\u044B\u0440\u0443\u0447\u043A\u0430 \u043F\u043E\u0441\u043B\u0435 \u0441\u043A\u0438\u0434\u043A\u0438, \u20BD",
        color: "var(--chart-3, #1F1F1F)"
      },
      discount: {
        label: "\u0421\u043A\u0438\u0434\u043A\u0438, \u20BD",
        color: "var(--chart-1, #FA5C2E)"
      }
    };
    $[2] = seller;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const money = t2;
  const t3 = seller ? "\u041F\u0440\u043E\u0434\u0430\u0432\u0446\u044B \u043F\u043E \u0434\u043D\u044F\u043C" : "\u0417\u0430\u043A\u0430\u0437\u044B \u043F\u043E \u0434\u043D\u044F\u043C";
  let t4;
  if ($[4] !== t3) {
    t4 = <CardHeader><CardTitle className="text-sm font-medium">{t3}</CardTitle></CardHeader>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = stats.daily;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      top: 4,
      right: 4,
      bottom: 0,
      left: -20
    };
    t7 = <CartesianGrid vertical={false} strokeDasharray="3 3" />;
    t8 = <XAxis dataKey="day" tickFormatter={shortDay} {...axisProps} minTickGap={16} />;
    t9 = <YAxis allowDecimals={false} {...axisProps} />;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  let t11;
  if ($[10] !== config) {
    t10 = <ChartTooltip config={config} labelFormatter={shortDay} />;
    t11 = Object.entries(config).map(_temp2);
    $[10] = config;
    $[11] = t10;
    $[12] = t11;
  } else {
    t10 = $[11];
    t11 = $[12];
  }
  let t12;
  if ($[13] !== stats.daily || $[14] !== t10 || $[15] !== t11) {
    t12 = <ChartContainer height={200}><BarChart data={t5} margin={t6}>{t7}{t8}{t9}{t10}{t11}</BarChart></ChartContainer>;
    $[13] = stats.daily;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== config) {
    t13 = <ChartLegend config={config} />;
    $[17] = config;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <CardContent className="flex flex-col gap-3">{t12}{t13}</CardContent>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t4) {
    t15 = <Card className="min-w-0">{t4}{t14}</Card>;
    $[22] = t14;
    $[23] = t4;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <CardHeader><CardTitle className="text-sm font-medium">Деньги по дням</CardTitle></CardHeader>;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = stats.daily;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      top: 4,
      right: 4,
      bottom: 0,
      left: 0
    };
    t19 = <CartesianGrid vertical={false} strokeDasharray="3 3" />;
    t20 = <XAxis dataKey="day" tickFormatter={shortDay} {...axisProps} minTickGap={16} />;
    t21 = <YAxis {...axisProps} tickFormatter={_temp3} />;
    $[26] = t18;
    $[27] = t19;
    $[28] = t20;
    $[29] = t21;
  } else {
    t18 = $[26];
    t19 = $[27];
    t20 = $[28];
    t21 = $[29];
  }
  let t22;
  let t23;
  if ($[30] !== money) {
    t22 = <ChartTooltip config={money} labelFormatter={shortDay} formatter={rub} />;
    t23 = Object.entries(money).map(_temp4);
    $[30] = money;
    $[31] = t22;
    $[32] = t23;
  } else {
    t22 = $[31];
    t23 = $[32];
  }
  let t24;
  if ($[33] !== stats.daily || $[34] !== t22 || $[35] !== t23) {
    t24 = <ChartContainer height={200}><BarChart data={t17} margin={t18}>{t19}{t20}{t21}{t22}{t23}</BarChart></ChartContainer>;
    $[33] = stats.daily;
    $[34] = t22;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] !== money) {
    t25 = <ChartLegend config={money} />;
    $[37] = money;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t24 || $[40] !== t25) {
    t26 = <Card className="min-w-0">{t16}<CardContent className="flex flex-col gap-3">{t24}{t25}</CardContent></Card>;
    $[39] = t24;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== t15 || $[43] !== t26) {
    t27 = <div className="grid gap-6 xl:grid-cols-2">{t15}{t26}</div>;
    $[42] = t15;
    $[43] = t26;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  return t27;
}
function _temp4(t0) {
  const [key_0, s_0] = t0;
  return <Bar key={key_0} dataKey={key_0} fill={s_0.color} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />;
}
function _temp3(v) {
  return num(v);
}
function _temp2(t0) {
  const [key, s] = t0;
  return <Bar key={key} dataKey={key} fill={s.color} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />;
}
function Section(t0) {
  const $ = _c(11);
  const {
    title,
    count,
    children,
    testId
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = count != null && <span className="ml-1.5 text-muted-foreground">{num(count)}</span>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== title) {
    t2 = <h2 className="text-sm font-medium">{title}{t1}</h2>;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children) {
    t3 = <div className="overflow-x-auto rounded-lg border">{children}</div>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== testId) {
    t4 = <section className="flex min-w-0 flex-col gap-2" data-testid={testId}>{t2}{t3}</section>;
    $[7] = t2;
    $[8] = t3;
    $[9] = testId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function Empty(t0) {
  const $ = _c(3);
  const {
    cols,
    text
  } = t0;
  let t1;
  if ($[0] !== cols || $[1] !== text) {
    t1 = <TableRow><TableCell colSpan={cols} className="py-6 text-center text-sm text-muted-foreground">{text}</TableCell></TableRow>;
    $[0] = cols;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function OrdersTable(t0) {
  const $ = _c(11);
  const {
    stats
  } = t0;
  const t1 = stats.orders.length;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableHeader><TableRow><TableHead>Заказ</TableHead><TableHead>Дата</TableHead><TableHead>Покупатель</TableHead><TableHead className="text-right">До скидки</TableHead><TableHead className="text-right">Скидка</TableHead><TableHead className="text-right">После скидки</TableHead><TableHead className="text-right">Возврат</TableHead><TableHead>Статус</TableHead></TableRow></TableHeader>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== stats.orders.length) {
    t3 = stats.orders.length === 0 && <Empty cols={8} text={"\u0417\u0430 \u043F\u0435\u0440\u0438\u043E\u0434 \u043F\u043E\u043A\u0443\u043F\u043E\u043A \u043F\u043E \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u0443 \u043D\u0435\u0442"} />;
    $[1] = stats.orders.length;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== stats.orders) {
    t4 = stats.orders.map(_temp5);
    $[3] = stats.orders;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <Table>{t2}<TableBody>{t3}{t4}</TableBody></Table>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== stats.orders.length || $[9] !== t5) {
    t6 = <Section title={"\u041F\u043E\u043A\u0443\u043F\u043A\u0438 \u043F\u043E \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u0443"} count={t1} testId="promo-orders">{t5}</Section>;
    $[8] = stats.orders.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function _temp5(o) {
  return <TableRow key={o.id} className={o.counted ? "" : "text-muted-foreground"}><TableCell className="font-mono text-xs"><Link className="hover:underline" to={`/orders?order=${encodeURIComponent(o.id)}`}>{o.id}</Link></TableCell><TableCell className="whitespace-nowrap text-sm">{dateTimeRu(o.createdAt)}</TableCell><TableCell className="text-sm">{o.buyerId ? <Link className="hover:underline" to={`/users/${o.buyerId}`}>{o.buyerName || "\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044C"}</Link> : <span>{o.buyerName || "\u0413\u043E\u0441\u0442\u044C"}</span>}<div className="text-xs text-muted-foreground">{o.buyerPhone}{o.isNewBuyer && <span className="ml-1.5 text-emerald-700">новый</span>}</div></TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(o.subtotal)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{o.discount > 0 ? `−${rub(o.discount)}` : "\u2014"}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(o.total)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{o.refund > 0 ? rub(o.refund) : "\u2014"}</TableCell><TableCell><Pill className={ORDER_GROUP_CLASS[o.statusGroup]}>{ORDER_GROUP_LABEL[o.statusGroup]}</Pill></TableCell></TableRow>;
}
function BuyersTable(t0) {
  const $ = _c(11);
  const {
    stats
  } = t0;
  const t1 = stats.buyers.length;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableHeader><TableRow><TableHead>Покупатель</TableHead><TableHead className="text-right">Заказы</TableHead><TableHead className="text-right">Выручка</TableHead><TableHead title={"\u041F\u0435\u0440\u0432\u044B\u0439 \u0437\u0430\u0441\u0447\u0438\u0442\u0430\u043D\u043D\u044B\u0439 \u0437\u0430\u043A\u0430\u0437 \u043F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044F \u043D\u0430 \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0435"}>Первый заказ</TableHead></TableRow></TableHeader>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== stats.buyers.length) {
    t3 = stats.buyers.length === 0 && <Empty cols={4} text={"\u041F\u043E\u043A\u0430 \u043D\u0438\u043A\u0442\u043E"} />;
    $[1] = stats.buyers.length;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== stats.buyers) {
    t4 = stats.buyers.map(_temp6);
    $[3] = stats.buyers;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <Table>{t2}<TableBody>{t3}{t4}</TableBody></Table>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== stats.buyers.length || $[9] !== t5) {
    t6 = <Section title={"\u041A\u0442\u043E \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043B"} count={t1} testId="promo-buyers">{t5}</Section>;
    $[8] = stats.buyers.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function _temp6(b, i) {
  return <TableRow key={b.buyerId ?? `${b.phone}-${i}`}><TableCell className="text-sm">{b.buyerId ? <Link className="hover:underline" to={`/users/${b.buyerId}`}>{b.name || "\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044C"}</Link> : <span>{b.name || "\u0413\u043E\u0441\u0442\u044C"}</span>}<div className="text-xs text-muted-foreground">{b.phone}{b.isNew ? <span className="ml-1.5 text-emerald-700">новый</span> : <span className="ml-1.5">повторный</span>}{b.attributed && <span className="ml-1.5">· пришёл по коду</span>}</div></TableCell><TableCell className="text-right tabular-nums">{num(b.orders)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(b.revenue)}</TableCell><TableCell className="text-sm">{dateRu(b.firstOrderAt)}</TableCell></TableRow>;
}
function TopProducts(t0) {
  const $ = _c(8);
  const {
    stats
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableHeader><TableRow><TableHead>Товар</TableHead><TableHead className="text-right">Шт.</TableHead><TableHead className="text-right">Сумма</TableHead></TableRow></TableHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== stats.products.length) {
    t2 = stats.products.length === 0 && <Empty cols={3} text={"\u041D\u0435\u0442 \u043F\u0440\u043E\u0434\u0430\u0436"} />;
    $[1] = stats.products.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== stats.products) {
    t3 = stats.products.slice(0, 10).map(_temp7);
    $[3] = stats.products;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Section title={"\u0422\u043E\u043F \u0442\u043E\u0432\u0430\u0440\u043E\u0432"} testId="promo-products"><Table>{t1}<TableBody>{t2}{t3}</TableBody></Table></Section>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp7(p) {
  return <TableRow key={p.productId}><TableCell className="text-sm"><Link className="hover:underline" to={`/products/${p.productId}`}>{p.name}</Link><div className="text-xs text-muted-foreground">{p.shopName}</div></TableCell><TableCell className="text-right tabular-nums">{num(p.qty)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(p.revenue)}</TableCell></TableRow>;
}
function TopShops(t0) {
  const $ = _c(8);
  const {
    stats
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableHeader><TableRow><TableHead>Магазин</TableHead><TableHead className="text-right">Заказы</TableHead><TableHead className="text-right">Шт.</TableHead><TableHead className="text-right">Сумма</TableHead></TableRow></TableHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== stats.shops.length) {
    t2 = stats.shops.length === 0 && <Empty cols={4} text={"\u041D\u0435\u0442 \u043F\u0440\u043E\u0434\u0430\u0436"} />;
    $[1] = stats.shops.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== stats.shops) {
    t3 = stats.shops.slice(0, 10).map(_temp8);
    $[3] = stats.shops;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Section title={"\u0422\u043E\u043F \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432"} testId="promo-shops-top"><Table>{t1}<TableBody>{t2}{t3}</TableBody></Table></Section>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp8(s) {
  return <TableRow key={s.shopId}><TableCell className="text-sm">{s.name}</TableCell><TableCell className="text-right tabular-nums">{num(s.orders)}</TableCell><TableCell className="text-right tabular-nums">{num(s.qty)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(s.revenue)}</TableCell></TableRow>;
}
function SellersTable(t0) {
  const $ = _c(11);
  const {
    stats
  } = t0;
  const t1 = stats.sellers.length;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TableHeader><TableRow><TableHead>Магазин</TableHead><TableHead>Пришёл</TableHead><TableHead>Статус</TableHead><TableHead className="text-right">Товары</TableHead><TableHead className="text-right">Заказы</TableHead><TableHead className="text-right">Шт.</TableHead><TableHead className="text-right">Продажи</TableHead><TableHead className="text-right">Комиссия</TableHead></TableRow></TableHeader>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== stats.sellers.length) {
    t3 = stats.sellers.length === 0 && <Empty cols={8} text={"\u041F\u043E \u043A\u043E\u0434\u0443 \u0435\u0449\u0451 \u043D\u0438\u043A\u0442\u043E \u043D\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043B\u0441\u044F"} />;
    $[1] = stats.sellers.length;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== stats.sellers) {
    t4 = stats.sellers.map(_temp9);
    $[3] = stats.sellers;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <Table>{t2}<TableBody>{t3}{t4}</TableBody></Table>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== stats.sellers.length || $[9] !== t5) {
    t6 = <Section title={"\u041F\u0440\u043E\u0434\u0430\u0432\u0446\u044B \u043F\u043E \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u0443"} count={t1} testId="promo-sellers">{t5}</Section>;
    $[8] = stats.sellers.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function _temp9(s) {
  return <TableRow key={s.userId}><TableCell className="text-sm"><Link className="hover:underline" to={`/shops/seller/${s.userId}`}>{s.shopName || "\u041C\u0430\u0433\u0430\u0437\u0438\u043D \u0431\u0435\u0437 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F"}</Link><div className="text-xs text-muted-foreground">{s.email} · {s.phone}</div></TableCell><TableCell className="whitespace-nowrap text-sm">{dateRu(s.attributedAt)}<div className="text-xs text-muted-foreground">{sourceLabel(s.source)}</div></TableCell><TableCell className="text-sm">{ONBOARDING_LABEL[s.onboardingStatus] ?? s.onboardingStatus}{s.approvedAt && <div className="text-xs text-muted-foreground">с {dateRu(s.approvedAt)}</div>}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{num(s.productsPublished)}<span className="text-muted-foreground"> / {num(s.productsTotal)}</span></TableCell><TableCell className="text-right tabular-nums">{num(s.orders)}</TableCell><TableCell className="text-right tabular-nums">{num(s.qty)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(s.gmv)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(s.commission)}</TableCell></TableRow>;
}